// lib/discoveryStorage.ts — persisted discovery quiz results.
// ─────────────────────────────────────────────────────────────────────────
// Follows the sessionMode anon/auth key-separation rule: anonymous visitors
// and logged-in users never share localStorage keys (shared-machine
// hygiene). Anonymity is derived synchronously from auth.currentUser via
// isAnonymousSession() at every read/write — never from a React-effect
// flag.
//
// We store RAW ANSWERS keyed by quiz id ({ quizId, answers[] }, versioned).
// Results are recomputed from content at render, so copy edits never
// invalidate saved results, storage stays tiny, and the same payload
// round-trips through the couples URL encoding in Session 5.
//
// REWARD HOOK: saveDiscoveryResult dispatches DISCOVERY_SAVED_EVENT (a
// window CustomEvent carrying the quizId). The quiz runner listens and fires
// the `discovery_quiz_completed` reward; the reward ledger dedupes, so
// re-saves (retakes, two-player pair upgrades) are harmless. Keeping the
// hook here means every engine and any future engine is covered without
// per-call-site wiring.
//
// Deliberately NOT wired into sessionMode's adopt/discard carry-over:
// quiz results are a marketing artifact, not financial data. If signup
// carry-over ever matters here, add these keys to adoptAnonData — one
// line, zero migration.
// ─────────────────────────────────────────────────────────────────────────

import type {
	DiscoveryQuizId,
	StoredDiscoveryResult,
} from '@nisk/core/quizzes';
import { isAnonymousSession } from './sessionMode';

export const DISCOVERY_KEY = 'nisk_discovery';
export const ANON_DISCOVERY_KEY = 'nisk_discovery_anon';

/** Window event fired after every successful saveDiscoveryResult write.
 *  detail: { quizId }. */
export const DISCOVERY_SAVED_EVENT = 'nis:discovery-saved';

export type DiscoveryResultMap = Partial<
	Record<DiscoveryQuizId, StoredDiscoveryResult>
>;

const activeKey = (): string =>
	isAnonymousSession() ? ANON_DISCOVERY_KEY : DISCOVERY_KEY;

export function readDiscoveryResults(): DiscoveryResultMap {
	try {
		const raw = localStorage.getItem(activeKey());
		if (!raw) return {};
		const parsed: unknown = JSON.parse(raw);
		if (typeof parsed !== 'object' || parsed === null) return {};
		return parsed as DiscoveryResultMap;
	} catch {
		return {};
	}
}

export function readDiscoveryResult(
	quizId: DiscoveryQuizId,
): StoredDiscoveryResult | null {
	const stored = readDiscoveryResults()[quizId];
	return stored && stored.v === 1 ? stored : null;
}

export function saveDiscoveryResult(result: StoredDiscoveryResult): void {
	try {
		const map = readDiscoveryResults();
		map[result.quizId] = result;
		localStorage.setItem(activeKey(), JSON.stringify(map));
		// Reward hook (see header). Fired only after a successful write.
		window.dispatchEvent(
			new CustomEvent(DISCOVERY_SAVED_EVENT, {
				detail: { quizId: result.quizId },
			}),
		);
	} catch {
		/* quota / private browsing — result simply isn't persisted */
	}
}

export function clearDiscoveryResult(quizId: DiscoveryQuizId): void {
	try {
		const map = readDiscoveryResults();
		delete map[quizId];
		localStorage.setItem(activeKey(), JSON.stringify(map));
	} catch {
		/* ignore */
	}
}
